import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Divider,
  Input,
  message,
  Segmented,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  DownloadOutlined,
  LeftOutlined,
  ReloadOutlined,
  RightOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import PlanningBoard, { GroupBy } from './PlanningBoard';
import PlanningOrderDrawer from './PlanningOrderDrawer';
import {
  useProductionOrders,
  useUpdateProductionOrder,
} from '../../../hooks/useProductionOrderHooks';
import { ProductionOrder } from '../../../types/ProductionOrder';
import {
  barGeometry,
  ColorMode,
  countOverdueOrders,
  sectionColorFor,
  sectionConflictKeys,
  STATUS_COLOR,
  Zoom,
  ZOOM_DAY_WIDTH,
} from './planning-board.util';

const PREF_KEY = 'mmd.planning-board.prefs';
type Prefs = {
  zoom?: Zoom;
  groupBy?: GroupBy;
  status?: string;
  colorMode?: ColorMode;
};
const loadPrefs = (): Prefs => {
  try {
    return JSON.parse(localStorage.getItem(PREF_KEY) || '{}') as Prefs;
  } catch {
    return {};
  }
};

const { RangePicker } = DatePicker;
const { Title } = Typography;

const STATUS_FILTERS = ['ALL', 'DRAFT', 'CONFIRM', 'APPROVED', 'DONE', 'CANCEL'];

/** embedded: shown as a tab of the Planning page, which carries the header. */
const PlanningBoardPage: React.FC<{ embedded?: boolean }> = ({
  embedded = false,
}) => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const prefs = useMemo(loadPrefs, []);
  const [zoom, setZoom] = useState<Zoom>(prefs.zoom ?? 'day');
  const [groupBy, setGroupBy] = useState<GroupBy>(prefs.groupBy ?? 'order');
  const [colorMode, setColorMode] = useState<ColorMode>(prefs.colorMode ?? 'status');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(prefs.status ?? 'ALL');

  useEffect(() => {
    localStorage.setItem(
      PREF_KEY,
      JSON.stringify({ zoom, groupBy, colorMode, status: statusFilter }),
    );
  }, [zoom, groupBy, colorMode, statusFilter]);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data, isLoading, isFetching, refetch } = useProductionOrders({
    resource: 'full',
    length: 500,
    sort: 'productionStart',
    direction: 'asc',
    search,
  });
  const update = useUpdateProductionOrder();

  const [rangeStart, rangeEnd] = range;
  const dayWidth = ZOOM_DAY_WIDTH[zoom];

  const orders = useMemo<ProductionOrder[]>(() => {
    const all = data?.data ?? [];
    return all.filter((o) => {
      if (statusFilter !== 'ALL' && o.status !== statusFilter) return false;
      const overlaps = barGeometry(
        o.productionStart,
        o.productionEnd,
        rangeStart,
        rangeEnd,
        dayWidth,
      ).visible;
      const noDates = !o.productionStart && !o.productionEnd;
      return overlaps || noDates;
    });
  }, [data, statusFilter, rangeStart, rangeEnd, dayWidth]);

  const conflicts = useMemo(() => sectionConflictKeys(orders).size, [orders]);
  const overdue = useMemo(() => countOverdueOrders(orders), [orders]);
  const sectionLegend = useMemo(() => {
    const map = new Map<string, string>();
    orders.forEach((o) =>
      o.sections?.forEach((s) => {
        const c = s.section?.code;
        if (c && !map.has(c)) map.set(c, sectionColorFor(c));
      }),
    );
    return Array.from(map.entries());
  }, [orders]);
  const processLegend = useMemo(() => {
    const map = new Map<string, string>();
    orders.forEach((o) =>
      o.sections?.forEach((s) => {
        const p = s.section?.prodProcess;
        if (p?.code && p?.boardColor && !map.has(p.code)) {
          map.set(p.code, p.boardColor);
        }
      }),
    );
    return Array.from(map.entries());
  }, [orders]);
  const legendItems =
    colorMode === 'section'
      ? sectionLegend
      : colorMode === 'process'
      ? processLegend
      : Object.entries(STATUS_COLOR);

  const openOrder = (o: ProductionOrder) => {
    setSelectedId(o.id);
    setDrawerOpen(true);
  };

  // dynamically import xlsx so the (heavy) library stays out of the main bundle
  const exportToExcel = async () => {
    const XLSX = await import('xlsx');
    const rows: Record<string, string | null>[] = [];
    orders.forEach((o) => {
      rows.push({
        Order: o.code,
        Section: '',
        Start: o.productionStart,
        End: o.productionEnd,
        Status: o.status,
      });
      o.sections?.forEach((s) =>
        rows.push({
          Order: o.code,
          Section: s.section?.code ?? '',
          Start: s.productionStart,
          End: s.productionEnd,
          Status: s.status,
        }),
      );
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Planning');
    XLSX.writeFile(wb, `planning-board-${dayjs().format('YYYYMMDD')}.xlsx`);
  };

  const onReschedule = (
    order: ProductionOrder,
    start: string,
    end: string,
  ) => {
    update.mutate(
      { ...order, productionStart: start, productionEnd: end },
      {
        onError: (e) =>
          message.error(
            e.response?.data?.message ?? 'Failed to reschedule order',
          ),
      },
    );
  };

  const onRescheduleSection = (
    order: ProductionOrder,
    sectionIndex: number,
    start: string,
    end: string,
  ) => {
    update.mutate(
      {
        ...order,
        sections: order.sections.map((s, i) =>
          i === sectionIndex
            ? { ...s, productionStart: start, productionEnd: end }
            : s,
        ),
      },
      {
        onError: (e) =>
          message.error(
            e.response?.data?.message ?? 'Failed to reschedule section',
          ),
      },
    );
  };

  const shift = (dir: 1 | -1) => {
    const span = rangeEnd.diff(rangeStart, 'day') + 1;
    setRange([rangeStart.add(dir * span, 'day'), rangeEnd.add(dir * span, 'day')]);
  };
  const goToday = () =>
    setRange([dayjs().startOf('month'), dayjs().endOf('month')]);

  return (
    <div className={'page-wrapper'}>
      <div className={'flex items-center justify-between mb-3 flex-wrap gap-2'}>
        {embedded ? (
          <span />
        ) : (
          <Title level={3} style={{ margin: 0 }}>
            Planning Board
          </Title>
        )}
        <Space wrap>
          <Input.Search
            allowClear
            placeholder={'Search order code…'}
            style={{ width: 180 }}
            onSearch={setSearch}
          />
          <Segmented
            options={STATUS_FILTERS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as string)}
          />
          <Segmented
            options={[
              { label: 'By Order', value: 'order' },
              { label: 'By Section', value: 'section' },
            ]}
            value={groupBy}
            onChange={(v) => setGroupBy(v as GroupBy)}
          />
          <Segmented
            options={[
              { label: 'Colour: Status', value: 'status' },
              { label: 'Colour: Section', value: 'section' },
              { label: 'Colour: Process', value: 'process' },
            ]}
            value={colorMode}
            onChange={(v) => setColorMode(v as ColorMode)}
          />
          <Segmented
            options={[
              { label: 'Day', value: 'day' },
              { label: 'Week', value: 'week' },
              { label: 'Month', value: 'month' },
            ]}
            value={zoom}
            onChange={(v) => setZoom(v as Zoom)}
          />
          <Space.Compact>
            <Button icon={<LeftOutlined />} onClick={() => shift(-1)} />
            <Button onClick={goToday}>Today</Button>
            <Button icon={<RightOutlined />} onClick={() => shift(1)} />
          </Space.Compact>
          <RangePicker
            allowClear={false}
            value={range}
            onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
          />
          <Button
            icon={<ReloadOutlined />}
            loading={isFetching}
            onClick={() => refetch()}
          />
          <Button icon={<DownloadOutlined />} onClick={exportToExcel}>
            Export
          </Button>
        </Space>
      </div>

      <Space split={<Divider type={'vertical'} />} className={'mb-2'} wrap>
        <Typography.Text type={'secondary'}>
          {orders.length} order{orders.length === 1 ? '' : 's'} in view
        </Typography.Text>
        {conflicts > 0 && (
          <Typography.Text style={{ color: '#faad14' }}>
            <WarningOutlined /> {conflicts} in conflict
          </Typography.Text>
        )}
        {overdue > 0 && (
          <Typography.Text style={{ color: '#ff4d4f' }}>
            ● {overdue} overdue
          </Typography.Text>
        )}
      </Space>

      <Space wrap className={'mb-2'}>
        {legendItems.length === 0 && colorMode === 'process' ? (
          <Typography.Text type={'secondary'}>
            No process assigned to sections yet
          </Typography.Text>
        ) : (
          legendItems.map(([label, c]) => (
            <Tag key={label} color={c} style={{ marginInlineEnd: 4 }}>
              {label}
            </Tag>
          ))
        )}
      </Space>

      {isLoading ? (
        <div className={'flex justify-center py-16'}>
          <Spin />
        </div>
      ) : (
        <PlanningBoard
          orders={orders}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          dayWidth={dayWidth}
          groupBy={groupBy}
          colorMode={colorMode}
          onSelectOrder={openOrder}
          onReschedule={onReschedule}
          onRescheduleSection={onRescheduleSection}
        />
      )}

      <PlanningOrderDrawer
        orderId={selectedId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
};

export default PlanningBoardPage;
